import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AgendaOnline from '../components/AgendaOnline';
import Payments from '../components/Payments';
import { Appointment } from '../types';
import { X } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';

export default function Layout() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [prefilledServiceId, setPrefilledServiceId] = useState<string>('');
  const [activeAppointment, setActiveAppointment] = useState<Appointment | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleOpenBooking = (serviceId?: string) => {
    if (serviceId) {
      setPrefilledServiceId(serviceId);
    } else {
      setPrefilledServiceId('');
    }
    setIsBookingOpen(true);
  };

  const handleOpenPaymentDirectly = (serviceId: string) => {
    const serviceNameMap: Record<string, { name: string; price: number }> = {
      'consulta-inicial': { name: 'Consulta de Evaluación Inicial', price: 45000 },
      'consulta-seguimiento': { name: 'Sesión de Control y Seguimiento', price: 30000 },
      'pack-bienestar': { name: 'Pack Bienestar Integral (1 Mes)', price: 68000 },
      'programa-transformacion': { name: 'Programa Hábitos Sostenibles (3 Meses)', price: 155000 },
      'nutricion-deportiva-plan': { name: 'Plan Atleta de Rendimiento', price: 85000 }
    };

    const details = serviceNameMap[serviceId] || { name: 'Plan Personalizado', price: 45000 };

    const dummyAppointment: Appointment = {
      id: 'app-' + Math.floor(Math.random() * 100000),
      serviceId,
      serviceName: details.name,
      date: 'Fecha por confirmar (Sincronizada vía WhatsApp)',
      timeSlot: 'Opcional / Por acordar',
      mode: 'online',
      patientName: 'Paciente Invitado',
      patientEmail: 'correo@ejemplo.cl',
      patientPhone: '955443322',
      patientRut: '19.481.029-K',
      status: 'pending',
      price: details.price
    };

    setActiveAppointment(dummyAppointment);
    setShowPaymentModal(true);
  };

  const handleAppointmentConfirmed = (appointment: Appointment) => {
    setActiveAppointment(appointment);
    setIsBookingOpen(false);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = (confirmedAppointment: Appointment) => {
    setActiveAppointment(confirmedAppointment);
  };

  return (
    <div className="min-h-screen flex flex-col justify-between selection:bg-sage-200 selection:text-sage-900 bg-sand-50">
      <Navbar onOpenBooking={() => handleOpenBooking()} />

      <main className="flex-grow">
        <Outlet context={{ 
          onOpenBooking: handleOpenBooking, 
          onOpenPaymentDirectly: handleOpenPaymentDirectly 
        }} />
      </main>

      <Footer />

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />

      {/* Booking Modal */}
      {isBookingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-sage-900/60 backdrop-blur-xs transition-opacity cursor-pointer"
            onClick={() => setIsBookingOpen(false)}
          ></div>
          <div className="relative bg-white rounded-2xl w-full max-w-4xl z-10 p-1 md:p-3 overflow-y-auto max-h-[95vh] animate-scale-up shadow-2xl">
            <button
              onClick={() => setIsBookingOpen(false)}
              className="absolute top-4 right-4 z-40 text-sage-400 hover:text-sage-700 bg-white shadow-xs p-1.5 rounded-full border border-sage-100"
              title="Cerrar Agenda"
            >
              <X className="w-5 h-5" />
            </button>
            <AgendaOnline 
              initialServiceId={prefilledServiceId} 
              onAppointmentConfirmed={handleAppointmentConfirmed}
              onClose={() => setIsBookingOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && activeAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-sage-900/60 backdrop-blur-xs transition-opacity cursor-pointer"
            onClick={() => setShowPaymentModal(false)}
          ></div>
          <div className="relative bg-white rounded-2xl w-full max-w-3xl z-10 p-2 md:p-5 overflow-y-auto max-h-[95vh] animate-scale-up shadow-2xl border border-sage-200">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 z-40 text-sage-400 hover:text-sage-700 bg-white shadow-xs p-1.5 rounded-full border border-sage-100"
              title="Cerrar Transacción"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="pt-6">
              <Payments 
                activeAppointment={activeAppointment} 
                onPaymentSuccess={handlePaymentSuccess}
                onCancel={() => setShowPaymentModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
